class InfiniteSlider {
  constructor(containerSelector) {
    // Get main container; throw if not found (fail fast)
    this.container = document.querySelector(containerSelector);
    if (!this.container) throw new Error('Slider container not found');

    this.track = this.container.querySelector('.slider-track');
    this.prev = document.querySelector('.personalize-slider .arrow.prev');
    this.next = document.querySelector('.personalize-slider .arrow.next');
    this.realCards = Array.from(this.track.children); // Original cards (no clones)
    this.isMobile = false;  
    this.animating = false;
    this.current = 1; // Start at first real card (after lastClone)
    this.resizeTimeout = null;

    this.bindEvents();
    this.setup();
  }

  bindEvents() {
    this.bindArrows();
    this.bindSwipe();
    // Debounced resize
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.setup(), 100);
    });
    // Listen for slider transition end to handle infinite loop jumps
    this.track.addEventListener('transitionend', (e) => {
      if (e.propertyName.includes('transform')) this.handleLoop();
    });
  }

  setup() {
    const isMobile = window.innerWidth < 769;
    if (isMobile) {
      if (!this.isMobile) this.initMobile();
      this.goto(this.current, false);
    } else {
      if (this.isMobile) this.cleanupDesktop();
      this.isMobile = false;
    }
  }

  initMobile() {
    // Rebuild track with clones for infinite loop illusion: [lastClone, ...realCards, firstClone]
    this.track.innerHTML = '';
    const firstClone = this.realCards[0].cloneNode(true);
    const lastClone = this.realCards.at(-1).cloneNode(true);
    firstClone.classList.add('clone');
    lastClone.classList.add('clone');
    this.track.appendChild(lastClone);
    this.realCards.forEach((card) => this.track.appendChild(card));
    this.track.appendChild(firstClone);
    this.cards = Array.from(this.track.children); // Includes clones
    this.isMobile = true;

    // Delegated click: only open card's anchor is clickable, others just move slider
    this.track.addEventListener('click', (e) => {
      // Find the card element that was clicked
      let cardElem = e.target;
      while (cardElem && !cardElem.classList.contains('card')) cardElem = cardElem.parentElement;
      if (!cardElem) return;

      // Find the slide index in the cards array
      const clickedIndex = Array.from(this.cards).indexOf(cardElem);

      // If not the open card, move slider and prevent navigation
      if (clickedIndex !== this.current) {
        e.preventDefault();
        if (!this.animating && clickedIndex !== -1) {
          this.goto(clickedIndex);
        }
        return false;
      }

      // If it is the open card, allow navigation ONLY if anchor tag was directly clicked/tapped
      let anchorElem = e.target;
      while (anchorElem && anchorElem.tagName !== 'A') anchorElem = anchorElem.parentElement;
      if (!anchorElem) {
        // Not clicked on anchor, prevent navigation
        e.preventDefault();
        return false;
      }
      // If we reach here, anchor was clicked on open card: allow navigation
      // (no e.preventDefault)
    });

    this.update(false);
  }


  cleanupDesktop() {
    // Restore to original cards (no clones, no transforms)
    this.track.innerHTML = '';
    this.realCards.forEach((card) => {
      card.classList.remove('clone');
      this.track.appendChild(card);
    });
    this.cards = this.realCards;
    this.track.style.transition = '';
    this.track.style.transform = '';
  }

  bindArrows() {
    this.prev.addEventListener('click', () => this.goto(this.current - 1));
    this.next.addEventListener('click', () => this.goto(this.current + 1));
  }

  bindSwipe() {
    // Handles both touch and pointer swipe gestures
    let startX = 0, startY = 0, currentX = 0, currentY = 0, deltaX = 0, deltaY = 0;
    let isHorizontal = null;
    let isPointerDown = false; // For pointer events

    // Touch events (mobile)
    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isHorizontal = null;
    }, { passive: true });

    this.track.addEventListener('touchmove', (e) => {
      currentX = e.touches[0].clientX;
      currentY = e.touches[0].clientY;
      deltaX = currentX - startX;
      deltaY = currentY - startY;

      // Determine direction after a threshold
      if (isHorizontal === null && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
        isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
      }

      if (isHorizontal) {
        // Only preventDefault for horizontal moves to stop page scroll left/right
        e.preventDefault();
      }
      // Else: vertical movement, allow native scroll (do not preventDefault)
    }, { passive: false });

    this.track.addEventListener('touchend', () => {
      if (!this.isMobile || this.animating || isHorizontal === false) return;
      if (deltaX > 40) this.goto(this.current - 1);
      if (deltaX < -40) this.goto(this.current + 1);
      // Reset
      deltaX = 0; deltaY = 0; isHorizontal = null;
    });

    // Pointer events (desktop dragging, left/right only)
    this.track.addEventListener('pointerdown', (e) => {
      isPointerDown = true;
      startX = e.clientX;
    });

    window.addEventListener('pointerup', (e) => {
      if (!isPointerDown || !this.isMobile || this.animating) return;
      isPointerDown = false;
      const dx = e.clientX - startX;
      if (dx > 40) this.goto(this.current - 1);
      if (dx < -40) this.goto(this.current + 1);
    });
  }

  goto(idx, animate = true) {
    if (!this.isMobile || this.animating) return;
    this.animating = animate;
    const len = this.cards.length;
    // Handle negative/overflowing indices for true infinite loop
    this.current = ((idx % len) + len) % len;
    this.update(animate);
  }

  update(animate = true) {
    this.track.style.transition = animate ? 'transform 0.6s cubic-bezier(.77,0,.175,1)' : 'none';

    this.cards.forEach((card, i) => {
      card.className = 'card';
      if (i === this.current) card.classList.add('open');
      else if (
        i === (this.current - 1 + this.cards.length) % this.cards.length ||
        i === (this.current + 1) % this.cards.length
      ) {
        card.classList.add('capsule');
      } else {
        card.classList.add('offscreen');
      }
    });

    // Calculate offsets live (no caching)
    const openCard = this.cards[this.current];
    const containerCenter = (this.container.clientWidth - openCard.offsetWidth) / 2;
    let offset = 0;
    for (let i = 0; i < this.current; i++) {
      offset += this.cards[i].offsetWidth + 16;
    }

    const leftWrapOffset =
      this.current === 0 ? this.cards[this.cards.length - 2].offsetWidth + 16 : 0;
    const rightWrapOffset =
      this.current === this.cards.length - 1 ? this.cards[1].offsetWidth + 16 : 0;

    this.track.style.transform = `translate3d(${containerCenter - offset + leftWrapOffset - rightWrapOffset}px, 0, 0)`;
  }

  handleLoop() {
    // Instantly jump to real card if user is at clone (after animation)
    this.animating = false;
    const len = this.cards.length;
    this.track.style.transition = 'none';
    if (this.current === 0) this.current = len - 2;
    else if (this.current === len - 1) this.current = 1;
    this.update(false);
  }
}

// Init slider + cult-number logic on DOM ready
window.addEventListener('DOMContentLoaded', () => {
  new InfiniteSlider('.slider-container');

  // --- Cult number dynamic logic below ---
  // This logic updates all elements with .cult-number based on days since START_DATE

  const START_DATE = '2025-07-02'; // Use ISO format for consistency
  const RANGES = [
    { min: 150, max: 250 },
    { min: 107, max: 199 },
    { min: 75, max: 287 },
    { min: 137, max: 180 },
  ];
  const INCREMENT = 3;

  function getDayDiff(startDate) {
    const start = new Date(startDate);
    const now = new Date();
    // Get current date in UTC to avoid timezone issues
    const nowUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    return Math.floor((nowUtc - start) / (1000 * 60 * 60 * 24));
  }

  function getCultNumber(idx, days) {
    // Increments cult numbers linearly but caps at max+ for each range
    const source = RANGES[idx];
    if (!source) return '';
    const { min, max } = source;

    const val = min + days * INCREMENT;
    return val >= max ? `${max}+` : val;
  }

  const daysSinceStart = getDayDiff(START_DATE);
  document.querySelectorAll('.cult-number').forEach((span, i) => {
    span.textContent = getCultNumber(i, daysSinceStart);
  });
});


const lottiePlayer = document.getElementById('my-lottie');

if (lottiePlayer) {
  lottiePlayer.addEventListener('ready', () => {
    const shadow = lottiePlayer.shadowRoot;
    if (!shadow) return;

    const animation = shadow.querySelector('#animation');
    if (animation) animation.style.height = 'auto';

    const svg = shadow.querySelector('svg');
    if (svg) svg.style.borderRadius = '12px';
  });
}
// Ensure lottie-player is defined before using it
